import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Mic, MicOff, Eye, EyeOff, Loader2, X } from 'lucide-react';
import { HandLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import { sentinelAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SentinelResponsePanel from './SentinelResponsePanel';

// Accepted wake-word variants. Browsers often mis-transcribe "veri":
// "very", "verify", "vary", "vari", etc. We accept all of these after "hey".
const WAKE_PATTERNS = [
  /\bhey\s+veri\b/i,
  /\bhey\s+very\b/i,
  /\bhey\s+verify\b/i,
  /\bhey\s+vary\b/i,
  /\bhey\s+vari\b/i,
  /\bhey\s+verifi\b/i,
  /\bhey\s+bury\b/i,     // Chrome sometimes hears this
  /\bverifypro\b/i,
];

// MediaPipe wasm files — served by jsDelivr CDN (mirrors the exact npm build).
// Local node_modules copies also exist under
// /app/frontend/node_modules/@mediapipe/tasks-vision/wasm — but CRA can't serve
// arbitrary node_modules paths, so CDN is the cleanest option here.
const MEDIAPIPE_WASM = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm';
const MEDIAPIPE_MODEL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

/**
 * VerifyPro Sentinel — ambient AI avatar. Voice + gesture + click.
 * Renders only when `user.sentinel_enabled === true`.
 */
export default function SentinelAvatar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [voiceOn, setVoiceOn] = useState(false);
  const [gestureOn, setGestureOn] = useState(false);
  const [gestureReady, setGestureReady] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [heardText, setHeardText] = useState('');
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');
  const [showHint, setShowHint] = useState(false);

  const recognitionRef = useRef(null);
  const commandStateRef = useRef({ awaiting: false, buffer: '', timer: null });
  const listenTimerRef = useRef(null);
  const videoRef = useRef(null);
  const gestureLoopRef = useRef(null);
  const gestureCooldownRef = useRef(0);
  const handLandmarkerRef = useRef(null);

  const enabled = !!user?.sentinel_enabled;

  // Suppress the floating orb inside the Argus War Room — that page has its
  // own immersive avatar + voice + gesture stack, and doubling up creates
  // confusing dual UIs.
  const suppressed = location.pathname.startsWith('/argus')
    || location.pathname.startsWith('/admin/dashboard')   // executive analytics — keep it clean
    || location.pathname.startsWith('/admin/analytics');

  const runCommand = useCallback(async (payload) => {
    setBusy(true);
    setError('');
    try {
      const { data } = await sentinelAPI.command(payload);
      setResponse(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Sentinel command failed.');
    } finally {
      setBusy(false);
      setIsListening(false);
      setHeardText('');
    }
  }, []);

  // ─── Voice: Web Speech API ────────────────────────────────────────────
  const startVoice = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError('Voice not supported. Use Chrome or Edge.');
      setVoiceOn(false);
      return;
    }
    if (recognitionRef.current) return;

    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.maxAlternatives = 3;

    const state = commandStateRef.current;
    const resetCommandMode = () => {
      state.awaiting = false;
      state.buffer = '';
      if (state.timer) { clearTimeout(state.timer); state.timer = null; }
      setIsListening(false);
    };

    // Restart the recognizer to clear accumulated results (avoids the
    // wake word matching over and over on old transcript).
    const softRestart = () => {
      try { rec.onend = null; rec.stop(); } catch { /* ignore */ }
      setTimeout(() => {
        if (recognitionRef.current !== rec) return;
        const fresh = new SR();
        fresh.continuous = true;
        fresh.interimResults = true;
        fresh.lang = 'en-US';
        fresh.maxAlternatives = 3;
        fresh.onresult = rec.onresult;
        fresh.onerror = rec.onerror;
        fresh.onend = rec.onend;
        recognitionRef.current = fresh;
        try { fresh.start(); } catch { /* ignore */ }
      }, 150);
    };

    rec.onresult = (evt) => {
      // Concatenate ALL results from this session for wake-word matching.
      let full = '';
      for (let i = 0; i < evt.results.length; i++) {
        full += evt.results[i][0].transcript + ' ';
      }
      const lower = full.toLowerCase();
      setHeardText(full.trim().slice(-80));

      if (!state.awaiting) {
        for (const p of WAKE_PATTERNS) {
          const m = p.exec(lower);
          if (m) {
            state.awaiting = true;
            setIsListening(true);
            // Command is whatever comes after the wake phrase
            state.buffer = lower.slice(m.index + m[0].length).trim();
            state.timer = setTimeout(() => {
              const cmd = state.buffer.trim();
              resetCommandMode();
              if (cmd) runCommand({ transcript: cmd });
              softRestart(); // clear history so next "hey veri" isn't shadowed
            }, 4500);
            break;
          }
        }
        return;
      }

      // Already in command mode — capture tail after wake phrase
      let tailFrom = -1;
      for (const p of WAKE_PATTERNS) {
        const m = p.exec(lower);
        if (m && m.index + m[0].length > tailFrom) tailFrom = m.index + m[0].length;
      }
      state.buffer = tailFrom >= 0 ? lower.slice(tailFrom).trim() : lower.trim();

      const last = evt.results[evt.results.length - 1];
      if (last?.isFinal && state.buffer) {
        const cmd = state.buffer.trim();
        resetCommandMode();
        runCommand({ transcript: cmd });
        softRestart();
      }
    };
    rec.onerror = (e) => {
      console.warn('sentinel voice error:', e.error);
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setError('Microphone permission denied.');
        setVoiceOn(false);
      } else if (e.error === 'no-speech') {
        // benign — just restart
      } else if (e.error === 'network') {
        setError('Voice service unreachable (network).');
      }
    };
    rec.onend = () => {
      if (recognitionRef.current === rec) {
        try { rec.start(); } catch { /* already started */ }
      }
    };
    try {
      rec.start();
      recognitionRef.current = rec;
    } catch (e) {
      console.warn('sentinel: rec.start failed', e);
      setError('Voice start failed.');
    }
  }, [runCommand]);

  const stopVoice = useCallback(() => {
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (rec) {
      try { rec.onend = null; rec.stop(); } catch { /* ignore */ }
    }
    const s = commandStateRef.current;
    if (s.timer) { clearTimeout(s.timer); s.timer = null; }
    s.awaiting = false; s.buffer = '';
    if (listenTimerRef.current) { clearTimeout(listenTimerRef.current); listenTimerRef.current = null; }
    setIsListening(false);
    setHeardText('');
  }, []);

  useEffect(() => {
    if (voiceOn) startVoice(); else stopVoice();
    return stopVoice;
  }, [voiceOn, startVoice, stopVoice]);

  // ─── Gesture: MediaPipe Hands (npm-bundled) ──────────────────────────
  const startGesture = useCallback(async () => {
    try {
      setGestureReady(false);
      setError('');
      const files = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM);
      const handLandmarker = await HandLandmarker.createFromOptions(files, {
        baseOptions: { modelAssetPath: MEDIAPIPE_MODEL },
        numHands: 1,
        runningMode: 'VIDEO',
      });
      handLandmarkerRef.current = handLandmarker;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 320, height: 240, facingMode: 'user' },
        audio: false,
      });
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();

      setGestureReady(true);

      const loop = () => {
        if (!handLandmarkerRef.current || !videoRef.current) return;
        try {
          const now = performance.now();
          const res = handLandmarkerRef.current.detectForVideo(videoRef.current, now);
          if (res && res.landmarks && res.landmarks.length > 0) {
            const lm = res.landmarks[0];
            if (isOpenPalm(lm) && now - gestureCooldownRef.current > 2500) {
              gestureCooldownRef.current = now;
              onGestureWake();
            }
          }
        } catch { /* ignore per-frame errors */ }
        gestureLoopRef.current = requestAnimationFrame(loop);
      };
      gestureLoopRef.current = requestAnimationFrame(loop);
    } catch (e) {
      console.warn('sentinel: gesture start failed', e);
      setError(`Gesture failed: ${e?.message || 'camera or model load blocked'}`);
      setGestureOn(false);
      setGestureReady(false);
    }
  }, []);

  const stopGesture = useCallback(() => {
    if (gestureLoopRef.current) {
      cancelAnimationFrame(gestureLoopRef.current);
      gestureLoopRef.current = null;
    }
    if (handLandmarkerRef.current) {
      try { handLandmarkerRef.current.close(); } catch { /* ignore */ }
      handLandmarkerRef.current = null;
    }
    const video = videoRef.current;
    if (video && video.srcObject) {
      video.srcObject.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
    }
    setGestureReady(false);
  }, []);

  useEffect(() => {
    if (gestureOn) startGesture(); else stopGesture();
    return stopGesture;
  }, [gestureOn, startGesture, stopGesture]);

  const onGestureWake = () => {
    // Palm-wave is a wake signal. If voice is on, force the recognizer into
    // command capture mode so the very next utterance is treated as command.
    setShowHint(true);
    setTimeout(() => setShowHint(false), 2500);
    if (voiceOn && recognitionRef.current) {
      const s = commandStateRef.current;
      s.awaiting = true;
      s.buffer = '';
      setIsListening(true);
      if (s.timer) clearTimeout(s.timer);
      s.timer = setTimeout(() => {
        const cmd = s.buffer.trim();
        s.awaiting = false; s.buffer = ''; s.timer = null;
        setIsListening(false);
        if (cmd) runCommand({ transcript: cmd });
      }, 5000);
    }
  };

  // ─── Click: one-shot text ─────────────────────────────────────────────
  const [oneShotOpen, setOneShotOpen] = useState(false);
  const [oneShotText, setOneShotText] = useState('');
  const oneShotInputRef = useRef(null);
  useEffect(() => {
    if (oneShotOpen && oneShotInputRef.current) oneShotInputRef.current.focus();
  }, [oneShotOpen]);
  const submitOneShot = (e) => {
    e?.preventDefault?.();
    if (!oneShotText.trim()) return;
    runCommand({ transcript: oneShotText.trim() });
    setOneShotOpen(false);
    setOneShotText('');
  };

  if (!enabled || suppressed) return null;

  return (
    <>
      {/* Live camera feed — visible small preview when gesture is on, so the
          user can see what MediaPipe is watching. */}
      <video
        ref={videoRef}
        playsInline
        muted
        style={{
          position: 'fixed',
          right: gestureOn ? 24 : -9999,
          bottom: gestureOn ? 200 : -9999,
          width: 160,
          height: 120,
          borderRadius: 12,
          border: '1px solid rgba(16,185,129,0.4)',
          boxShadow: gestureOn ? '0 4px 20px rgba(0,0,0,0.4)' : 'none',
          zIndex: 9995,
          objectFit: 'cover',
          transform: 'scaleX(-1)', // mirror so user sees themselves naturally
        }}
      />
      {gestureOn && !gestureReady ? (
        <div
          className="fixed right-6 z-[9997] px-3 py-1.5 rounded-full bg-slate-900/90 border border-amber-400/40 text-amber-200 text-xs backdrop-blur-md"
          style={{ bottom: 330 }}
          data-testid="sentinel-gesture-loading"
        >
          Loading gesture model…
        </div>
      ) : null}

      {response ? (
        <SentinelResponsePanel
          response={response}
          onClose={() => setResponse(null)}
          onNavigate={(url) => { if (url) navigate(url); setResponse(null); }}
        />
      ) : null}

      {oneShotOpen ? (
        <form
          onSubmit={submitOneShot}
          className="fixed bottom-32 right-6 z-[9998] w-[380px] max-w-[90vw] rounded-2xl border border-white/10 bg-slate-900/95 backdrop-blur-xl shadow-2xl p-3 flex items-center gap-2"
          data-testid="sentinel-oneshot-form"
        >
          <input
            ref={oneShotInputRef}
            value={oneShotText}
            onChange={(e) => setOneShotText(e.target.value)}
            placeholder='e.g. "show pending cases"'
            className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-sm px-2"
            data-testid="sentinel-oneshot-input"
          />
          <button
            type="submit"
            className="text-emerald-300 hover:text-emerald-200 text-xs font-semibold px-2"
            data-testid="sentinel-oneshot-submit"
          >
            Ask
          </button>
          <button
            type="button"
            onClick={() => { setOneShotOpen(false); setOneShotText(''); }}
            className="text-slate-500 hover:text-white"
            data-testid="sentinel-oneshot-close"
          >
            <X className="w-4 h-4" />
          </button>
        </form>
      ) : null}

      {/* Live transcript debug bubble — helps users know if the browser
          is hearing them (biggest cause of "voice not working"). */}
      {voiceOn && heardText && !response ? (
        <div
          className="fixed bottom-24 right-6 z-[9997] px-3 py-1.5 rounded-lg bg-slate-900/90 border border-white/10 text-slate-300 text-[11px] max-w-[280px] backdrop-blur-md truncate"
          data-testid="sentinel-transcript"
          title={heardText}
        >
          <span className="text-slate-500 mr-1">heard:</span>{heardText}
        </div>
      ) : null}

      {(showHint || isListening) && !response ? (
        <div
          className="fixed bottom-24 right-6 z-[9997] px-3 py-1.5 rounded-full bg-slate-900/90 border border-emerald-400/40 text-emerald-200 text-xs backdrop-blur-md"
          data-testid="sentinel-hint"
          style={{ animation: 'sentinelFadeIn 0.2s ease-out' }}
        >
          {isListening ? 'Listening for your command…' : 'Wake accepted — say your command'}
        </div>
      ) : null}

      <div className="fixed bottom-24 right-6 z-[9996] flex flex-col items-end gap-1.5">
        <ControlPill
          on={voiceOn}
          onClick={() => setVoiceOn((v) => !v)}
          onIcon={<Mic className="w-3.5 h-3.5" />}
          offIcon={<MicOff className="w-3.5 h-3.5" />}
          label={voiceOn ? 'Voice on · "Hey Veri"' : 'Voice off'}
          testid="sentinel-voice-toggle"
        />
        <ControlPill
          on={gestureOn}
          onClick={() => setGestureOn((v) => !v)}
          onIcon={<Eye className="w-3.5 h-3.5" />}
          offIcon={<EyeOff className="w-3.5 h-3.5" />}
          label={gestureOn ? 'Watching · wave open palm' : 'Gesture off'}
          testid="sentinel-gesture-toggle"
        />
        {error ? (
          <div className="max-w-[260px] text-[10px] text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-md px-2 py-1 backdrop-blur-md" data-testid="sentinel-error">
            {error}
          </div>
        ) : null}
      </div>

      <button
        onClick={() => setOneShotOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[9996] group focus:outline-none"
        title='VerifyPro Sentinel — click to type, say "Hey Veri" or wave'
        data-testid="sentinel-orb"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <SentinelOrb busy={busy} active={isListening || voiceOn || gestureOn} />
      </button>

      <style>{`
        @keyframes sentinelFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </>
  );
}

function ControlPill({ on, onClick, onIcon, offIcon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium backdrop-blur-md transition-all border ${
        on
          ? 'bg-emerald-500/15 text-emerald-200 border-emerald-400/40 shadow-[0_0_10px_rgba(16,185,129,0.35)]'
          : 'bg-slate-900/70 text-slate-400 border-white/10 hover:text-white'
      }`}
    >
      {on ? onIcon : offIcon}
      <span>{label}</span>
    </button>
  );
}

function SentinelOrb({ busy, active }) {
  return (
    <div
      className="relative w-16 h-16 flex items-center justify-center"
      aria-label="VerifyPro Sentinel"
      data-testid="sentinel-orb-visual"
    >
      <span
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(16,185,129,0.35) 0%, rgba(16,185,129,0) 70%)',
          animation: `${active ? 'sentinelPulseFast' : 'sentinelPulse'} 2.4s ease-in-out infinite`,
        }}
      />
      <span
        className="absolute inset-2 rounded-full"
        style={{
          background:
            'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9) 0%, rgba(52,211,153,0.85) 35%, rgba(16,78,68,0.9) 90%)',
          boxShadow:
            '0 0 30px rgba(16,185,129,0.55), 0 0 60px rgba(16,185,129,0.25), inset 0 0 15px rgba(255,255,255,0.35)',
        }}
      />
      <span
        className="absolute rounded-full"
        style={{
          width: 14, height: 14,
          background: 'radial-gradient(circle, #ffffff 0%, #6ee7b7 50%, #059669 100%)',
          filter: 'blur(0.3px)',
          animation: `${busy ? 'sentinelCorePulse' : 'sentinelCoreBreath'} ${busy ? '0.8s' : '3s'} ease-in-out infinite`,
        }}
      />
      <style>{`
        @keyframes sentinelPulse {
          0%, 100% { transform: scale(0.85); opacity: 0.55; }
          50%      { transform: scale(1.08); opacity: 0.9; }
        }
        @keyframes sentinelPulseFast {
          0%, 100% { transform: scale(0.9); opacity: 0.7; }
          50%      { transform: scale(1.18); opacity: 1;   }
        }
        @keyframes sentinelCoreBreath {
          0%, 100% { transform: scale(1);    filter: brightness(1); }
          50%      { transform: scale(1.15); filter: brightness(1.4); }
        }
        @keyframes sentinelCorePulse {
          0%, 100% { transform: scale(0.9); filter: brightness(1.1); }
          50%      { transform: scale(1.3); filter: brightness(1.9); }
        }
      `}</style>
      {busy ? (
        <Loader2 className="absolute w-5 h-5 text-white animate-spin" />
      ) : null}
    </div>
  );
}

// Open-palm heuristic. Landmarks index reference (MediaPipe):
//   0 wrist; 4 thumb tip; 5-8 index; 9-12 middle; 13-16 ring; 17-20 pinky.
function isOpenPalm(lm) {
  if (!lm || lm.length < 21) return false;
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const wrist = lm[0];
  const extended = (tipIdx, pipIdx) =>
    dist(lm[tipIdx], wrist) > dist(lm[pipIdx], wrist) * 1.15;
  const index  = extended(8, 6);
  const middle = extended(12, 10);
  const ring   = extended(16, 14);
  const pinky  = extended(20, 18);
  const thumbSpread = dist(lm[4], lm[5]) > dist(lm[3], lm[5]) * 1.05;
  return index && middle && ring && pinky && thumbSpread;
}
